/**
 * ReminderList Component - T129
 *
 * List of payment reminders for an invoice
 */
import { useState } from 'react';
import { GlassPanel, Button, LoadingSpinner, Modal } from '../ui';
import { ReminderCard } from './ReminderCard';
import { CreateReminderButton } from './CreateReminderButton';
import { useRemindersForInvoice, useDeleteReminder, type PaymentReminder } from '../../hooks/useReminders';

interface ReminderListProps {
  invoiceId: string;
  invoiceGrossTotal: number;
  invoiceStatus: string;
}

export function ReminderList({ invoiceId, invoiceGrossTotal, invoiceStatus }: ReminderListProps) {
  const { data: reminders = [], isLoading, error } = useRemindersForInvoice(invoiceId);
  const deleteReminder = useDeleteReminder();
  const [deleteTarget, setDeleteTarget] = useState<PaymentReminder | null>(null);

  const canCreateReminder = ['sent', 'overdue'].includes(invoiceStatus) && reminders.length < 3;

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteReminder.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  };

  // Only show delete option for the latest reminder
  const latestLevel = Math.max(0, ...reminders.map((r) => r.reminderLevel));

  if (isLoading) {
    return (
      <GlassPanel className="p-6">
        <div className="flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </GlassPanel>
    );
  }

  if (error) {
    return (
      <GlassPanel className="p-6">
        <p className="text-red-400 text-center">Fehler beim Laden der Mahnungen</p>
      </GlassPanel>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">
          Mahnungen {reminders.length > 0 && `(${reminders.length})`}
        </h3>
        {canCreateReminder && (
          <CreateReminderButton
            invoiceId={invoiceId}
            invoiceGrossTotal={invoiceGrossTotal}
          />
        )}
      </div>

      {/* List */}
      {reminders.length === 0 ? (
        <GlassPanel variant="subtle" className="p-6 text-center">
          <p className="text-white/40">
            {['sent', 'overdue'].includes(invoiceStatus)
              ? 'Keine Mahnungen vorhanden. Erstellen Sie eine Mahnung, wenn die Zahlung überfällig ist.'
              : 'Mahnungen können nur für gesendete oder überfällige Rechnungen erstellt werden.'}
          </p>
        </GlassPanel>
      ) : (
        <div className="space-y-3">
          {reminders.map((reminder) => (
            <ReminderCard
              key={reminder.id}
              reminder={reminder}
              onDelete={
                reminder.reminderLevel === latestLevel
                  ? () => setDeleteTarget(reminder)
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Mahnung löschen"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Möchten Sie diese Mahnung wirklich löschen?
          </p>
          <p className="text-sm text-white/40">
            Hinweis: Nur die letzte Mahnstufe kann gelöscht werden.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              loading={deleteReminder.isPending}
            >
              Löschen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
